import { useState, useRef } from 'react';
import { Search, Receipt, Upload, Image, Eye, EyeOff, Clock } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useOffline } from '../../contexts/OfflineContext';
import { uploadReceiptPhoto } from '../../utils/storage';
import { isActiveWO } from '../../utils/dataQueries';
import { colors, Header, Card, PillToggle, Button } from '../shared';

const ReceiptsScreen = () => {
  const {
    workOrders,
    getOpenWOsForTech,
    getUnmatchedBankCharges,
    getReceiptsForWO,
    laborEntries,
    addReceipt,
    getCurrentTech,
  } = useData();
  const { isOnline, queuePhoto, canQueuePhoto } = useOffline();

  const [filter, setFilter] = useState('missing');
  const [selectedWO, setSelectedWO] = useState(null);
  const [selectedMissing, setSelectedMissing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');

  const fileInputRef = useRef(null);
  const currentTech = getCurrentTech();

  const unmatchedCharges = getUnmatchedBankCharges();
  const openWOs = getOpenWOsForTech();
  const allWOs = workOrders.filter(wo =>
    wo.tech?.toLowerCase() === currentTech?.name?.toLowerCase()
  );

  const filteredWOs = filter === 'all' ? allWOs :
                      filter === 'open' ? openWOs : [];

  const searchedWOs = search
    ? filteredWOs.filter(wo =>
        wo.id.toLowerCase().includes(search.toLowerCase()) ||
        wo.description.toLowerCase().includes(search.toLowerCase()) ||
        wo.property.toLowerCase().includes(search.toLowerCase())
      )
    : filteredWOs;

  // Get WOs for a specific date (for matching bank charges)
  const getWOsForDate = (dateStr) => {
    const date = new Date(dateStr).toDateString();
    return laborEntries
      .filter(entry => {
        const entryDate = new Date(entry.startTime).toDateString();
        return entryDate === date && entry.techName?.toLowerCase() === currentTech?.name?.toLowerCase();
      })
      .map(entry => workOrders.find(wo => wo.id === entry.woId))
      .filter(Boolean);
  };

  const handlePhotoUpload = async (file, woId, bankChargeId = null) => {
    setUploading(true);

    try {
      let photoUrl = null;
      let photoPath = null;

      if (isOnline) {
        const result = await uploadReceiptPhoto(file, woId);
        photoUrl = result.url;
        photoPath = result.path;
      } else if (canQueuePhoto) {
        const queued = queuePhoto({
          type: 'receipt',
          woId,
          bankChargeId,
          file,
        });
        if (!queued.success) {
          alert(queued.error);
          setUploading(false);
          return;
        }
      }

      await addReceipt({
        woId,
        bankChargeId,
        photoUrl,
        photoPath,
        filename: file.name,
        status: 'active',
      });

      // Close the detail view after successful upload
      if (selectedMissing) {
        setSelectedMissing(null);
      }

    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Failed to upload receipt. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Missing Receipt Detail View
  if (selectedMissing) {
    const matchingWOs = getWOsForDate(selectedMissing.date);

    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header
          title="Match Receipt"
          subtitle={`${selectedMissing.vendor} • ${selectedMissing.date}`}
          showBack
          onBack={() => setSelectedMissing(null)}
        />
        <div className="px-4">
          {/* Bank Charge Info */}
          <div className="rounded-2xl p-4 mb-4" style={{ background: colors.redDim }}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold" style={{ color: colors.red }}>Unmatched Bank Charge</span>
              <span className="text-2xl font-bold" style={{ color: colors.red }}>${selectedMissing.amount}</span>
            </div>
            <div className="flex items-center gap-4 text-sm flex-wrap" style={{ color: colors.textSecondary }}>
              <span>{selectedMissing.vendor}</span>
              <span>•</span>
              <span>{selectedMissing.vendorId}</span>
              <span>•</span>
              <span>Card ****{selectedMissing.cardLast4}</span>
            </div>
          </div>

          {/* Upload Receipt */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && matchingWOs.length > 0) {
                handlePhotoUpload(file, matchingWOs[0].id, selectedMissing.id);
              }
            }}
            className="hidden"
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed rounded-2xl p-6 text-center mb-4 cursor-pointer"
            style={{ borderColor: colors.yellow, background: colors.yellowDim }}
          >
            <Upload size={32} style={{ color: colors.yellow }} className="mx-auto mb-2" />
            <p className="font-semibold mb-1" style={{ color: colors.yellow }}>
              {uploading ? 'Uploading...' : 'Upload receipt for this charge'}
            </p>
            <p className="text-sm" style={{ color: colors.textSecondary }}>We'll match it automatically</p>
          </div>

          {/* WOs from that day */}
          <p className="text-sm font-semibold mb-3" style={{ color: colors.textSecondary }}>
            Work Orders from {selectedMissing.date} ({matchingWOs.length})
          </p>
          <div className="space-y-2 mb-4">
            {matchingWOs.map(wo => (
              <button
                key={wo.id}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                className="w-full rounded-2xl p-4 text-left"
                style={{ background: colors.cardBg }}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-mono font-semibold" style={{ color: colors.yellow }}>{wo.id}</span>
                </div>
                <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>{wo.description}</p>
                <p className="text-sm" style={{ color: colors.textMuted }}>{wo.property}</p>
              </button>
            ))}
            {matchingWOs.length === 0 && (
              <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>
                No work orders found for this date
              </p>
            )}
          </div>

          {/* Explanation */}
          <div className="rounded-xl p-4" style={{ background: colors.inputBg }}>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              💡 <strong>How this works:</strong> We detected a bank charge from {selectedMissing.vendor} on {selectedMissing.date}. Above are the work orders where you logged time that day. Upload the receipt and we'll automatically match it to the correct WO, or you can select one manually.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // WO Receipt Detail View
  if (selectedWO) {
    const woReceipts = getReceiptsForWO(selectedWO.id);
    const activeReceipts = woReceipts.filter(r => r.status === 'active');
    const notRelevantReceipts = woReceipts.filter(r => r.status === 'not-relevant');

    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header
          title={selectedWO.id}
          subtitle={selectedWO.property}
          showBack
          onBack={() => setSelectedWO(null)}
        />
        <div className="px-4">
          <Card className="mb-4">
            <div className="flex items-start justify-between mb-2">
              <p className="font-medium" style={{ color: colors.textPrimary }}>{selectedWO.description}</p>
              <span
                className="px-2 py-1 rounded-full text-xs font-semibold"
                style={{
                  background: isActiveWO(selectedWO) ? colors.greenDim : colors.yellowDim,
                  color: isActiveWO(selectedWO) ? colors.green : colors.yellow
                }}
              >
                {isActiveWO(selectedWO) ? selectedWO.status : 'Completed'}
              </span>
            </div>
            <p className="text-sm" style={{ color: colors.textMuted }}>{selectedWO.createdDate}</p>
          </Card>

          {/* Upload Area */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhotoUpload(file, selectedWO.id);
            }}
            className="hidden"
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed rounded-2xl p-8 text-center mb-4 cursor-pointer"
            style={{ borderColor: colors.yellow, background: colors.yellowDim }}
          >
            <Upload size={40} style={{ color: colors.yellow }} className="mx-auto mb-3" />
            <p className="font-semibold mb-1" style={{ color: colors.yellow }}>
              {uploading ? 'Uploading...' : 'Tap to upload receipt'}
            </p>
            <p className="text-sm" style={{ color: colors.textSecondary }}>We'll process it with AI automatically</p>
          </div>

          {/* Active Receipts */}
          {activeReceipts.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold mb-3" style={{ color: colors.textSecondary }}>
                Uploaded Receipts ({activeReceipts.length})
              </p>
              <div className="space-y-2">
                {activeReceipts.map(receipt => (
                  <div key={receipt.id} className="rounded-xl p-3 flex items-center gap-3" style={{ background: colors.cardBg }}>
                    <div className="p-2 rounded-lg" style={{ background: colors.greenDim }}>
                      <Image size={20} style={{ color: colors.green }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm" style={{ color: colors.textPrimary }}>{receipt.filename}</p>
                      <p className="text-xs" style={{ color: colors.textMuted }}>
                        {new Date(receipt.createdAt?.seconds * 1000 || receipt.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {woReceipts.length === 0 && (
            <div className="text-center py-8">
              <Receipt size={48} style={{ color: colors.textMuted }} className="mx-auto mb-3 opacity-50" />
              <p style={{ color: colors.textMuted }}>No receipts uploaded yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
      <Header title="Receipts" subtitle="Upload & manage receipts" />
      <div className="px-4">
        <div className="mb-4">
          <PillToggle
            options={[
              { value: 'missing', label: `Missing (${unmatchedCharges.length})` },
              { value: 'open', label: 'Open WOs' },
              { value: 'all', label: 'All WOs' }
            ]}
            value={filter}
            onChange={setFilter}
          />
        </div>

        {filter === 'missing' ? (
          <>
            <p className="text-sm font-semibold mb-3" style={{ color: colors.textSecondary }}>
              Unmatched Bank Charges ({unmatchedCharges.length})
            </p>
            <div className="space-y-2 mb-4">
              {unmatchedCharges.map(charge => (
                <button
                  key={charge.id}
                  onClick={() => setSelectedMissing(charge)}
                  className="w-full rounded-2xl p-4 text-left"
                  style={{ background: colors.cardBg }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ background: colors.redDim }}>
                        <Receipt size={20} style={{ color: colors.red }} />
                      </div>
                      <div>
                        <p className="font-semibold" style={{ color: colors.textPrimary }}>{charge.vendor}</p>
                        <p className="text-xs" style={{ color: colors.textMuted }}>{charge.vendorId}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg" style={{ color: colors.red }}>${charge.amount}</p>
                      <p className="text-xs" style={{ color: colors.textMuted }}>{charge.date}</p>
                    </div>
                  </div>
                </button>
              ))}
              {unmatchedCharges.length === 0 && (
                <div className="text-center py-8">
                  <Receipt size={48} className="mx-auto mb-3 opacity-30" style={{ color: colors.green }} />
                  <p style={{ color: colors.green }}>All receipts matched!</p>
                </div>
              )}
            </div>
            {unmatchedCharges.length > 0 && (
              <div className="rounded-xl p-4" style={{ background: colors.inputBg }}>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  💡 These are bank charges from company cards that don't have matching receipts yet. Tap one to upload a receipt and match it to a work order.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="relative mb-4">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
              <input
                type="text"
                placeholder="Search work orders..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl outline-none"
                style={{ background: colors.cardBg, color: colors.textPrimary }}
              />
            </div>
            <p className="text-sm font-semibold mb-3" style={{ color: colors.textSecondary }}>
              {filter === 'open' ? 'Open' : 'All'} Work Orders ({searchedWOs.length})
            </p>
            <div className="space-y-2">
              {searchedWOs.map(wo => {
                const woReceiptCount = getReceiptsForWO(wo.id).filter(r => r.status === 'active').length;
                return (
                  <button
                    key={wo.id}
                    onClick={() => setSelectedWO(wo)}
                    className="w-full rounded-2xl p-4 text-left"
                    style={{ background: colors.cardBg }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold" style={{ color: colors.yellow }}>{wo.id}</span>
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            background: isActiveWO(wo) ? colors.greenDim : colors.inputBg,
                            color: isActiveWO(wo) ? colors.green : colors.textMuted
                          }}
                        >
                          {wo.status || 'Unknown'}
                        </span>
                      </div>
                      {woReceiptCount > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: colors.yellowDim }}>
                          <Receipt size={14} style={{ color: colors.yellow }} />
                          <span className="text-xs font-semibold" style={{ color: colors.yellow }}>{woReceiptCount}</span>
                        </div>
                      )}
                    </div>
                    <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>{wo.description}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-sm" style={{ color: colors.textMuted }}>{wo.property}</p>
                      <div className="flex items-center gap-1">
                        <Clock size={12} style={{ color: colors.textMuted }} />
                        <span className="text-xs" style={{ color: colors.textMuted }}>{wo.createdDate}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
              {searchedWOs.length === 0 && (
                <div className="text-center py-8">
                  <Receipt size={48} className="mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textMuted }}>No work orders found</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReceiptsScreen;
